import { datadogLogs } from '@datadog/browser-logs'
import { FdChat } from 'containers/FdChat'
import { AppState, AppStateContext } from 'containers/State/AppState'
import ErrorOverlay from 'controls/ErrorOverlay/ErrorOverlay'
import LoadingOverlay from 'controls/LoadingOverlay/LoadingOverlay'
import { useOptionalQuestionnaireParams } from 'hooks/useQuestionnaireParams'
import React, { PropsWithChildren, useContext } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { noContextMethod } from 'utils/context'

export interface PfmData {
  next: () => void
}

export const PfmContext = React.createContext<PfmData>({
  next: noContextMethod
})

const PageFlowManager = (props: PropsWithChildren<{}>) => {
  const { children }: PropsWithChildren<{}> = props
  const location = useLocation()
  const navigate = useNavigate()
  const questionnaireParams = useOptionalQuestionnaireParams()
  const isPreviewMode = questionnaireParams?.preview
  const {
    respondentProgress: [progress],
    renderedQuestionnaire: [data, loading, error],
    surveyLocales: [surveyLocales, surveyLocalesLoading],
    selectedLanguage: [selectedLanguage]
  } = useContext<AppState>(AppStateContext)

  const pushHistory: (pathName: string) => void = (pathName) => {
    navigate({
      pathname: pathName,
      search: location.search
    })
  }

  const isSurveyCompleted = progress
    ? progress.isCompleted ||
      progress.isQualityTerminated ||
      progress.isQuotaFull ||
      progress.isScreened
    : false

  // isSurveyFull only matters if the app is not in preview mode
  const isSurveyFull = data && data.isSurveyFull && !isPreviewMode
  const shouldProceedWithFdchat =
    (progress?.isCompleted || data?.questionnaire.entries.length === 0) &&
    data?.redirectToFDChat &&
    progress?.isTermsAgreed

  // The selector must show whenever the language is unresolved for an active
  // respondent: AppState defers the questionnaire fetch until
  // `selectedLanguage` resolves, so skipping the selector would leave the app
  // with no questionnaire at all. In particular `isTermsAgreed` must not gate
  // this — preview seeds it on mount and returning respondents hydrate it
  // from localStorage, but neither implies a language was ever picked. A pick
  // persisted on this device resolves silently in AppState; the selector
  // re-prompts only when none is usable (preview, cross-device resume,
  // cleared storage, or a withdrawn language). Respondents whose persisted
  // progress is terminal (isSurveyCompleted) are the exception: /complete
  // renders and fires the panel redirect without the questionnaire, so
  // prompting them would only delay — or, on a fetch failure, block — that
  // redirect. A survey-full response can't gate this: `data` requires a
  // resolved language, so a fresh visitor to a full survey picks a language
  // before landing on /complete.
  const needsLanguageSelection =
    !!surveyLocales &&
    surveyLocales.availableLanguages.length > 1 &&
    !selectedLanguage &&
    !isSurveyCompleted

  const getPageRedirect: () => JSX.Element | undefined = () => {
    // language selector takes precedence over the welcome page when the survey
    // supports multiple languages and the respondent hasn't picked one yet.
    // The other redirects below are gated on `!needsLanguageSelection` so they
    // can't ping-pong against this one.
    if (needsLanguageSelection && location.pathname !== '/language') {
      return <Navigate replace to={`/language${location.search}`} />
    }

    if (!needsLanguageSelection && location.pathname === '/language') {
      return <Navigate replace to={`/info${location.search}`} />
    }

    if (needsLanguageSelection) {
      return undefined
    }

    // if terms have not been agreed, go to info
    if (
      progress &&
      !progress.isTermsAgreed &&
      !isSurveyFull &&
      location.pathname !== '/terms' &&
      location.pathname !== '/info'
    ) {
      return <Navigate replace to={`/info${location.search}`} />
    }

    // if terms have been agreed and survey is not complete, go to questionnaire
    if (
      progress &&
      progress.isTermsAgreed &&
      !isSurveyCompleted &&
      !isSurveyFull &&
      location.pathname !== '/questionnaire'
    ) {
      return <Navigate replace to={`/questionnaire${location.search}`} />
    }

    // if survey is complete, go to complete page
    if (
      progress &&
      (isSurveyCompleted || isSurveyFull) &&
      location.pathname !== '/complete'
    ) {
      datadogLogs.logger.info('getPageRedirect: Forwarding to complete', {
        progress,
        isSurveyCompleted,
        isSurveyFull,
        data
      })
      return <Navigate replace to={`/complete${location.search}`} />
    }

    return undefined
  }

  const handleNext: () => void = () => {
    if (location.pathname === '/info' && data) {
      pushHistory('/terms')
    } else if (location.pathname === '/terms' && progress?.isTermsAgreed) {
      pushHistory('/questionnaire')
    } else if (
      location.pathname === '/questionnaire' &&
      !data?.redirectToFDChat
    ) {
      datadogLogs.logger.info('handleNext: Forwarding to complete', {
        progress,
        isSurveyCompleted,
        isSurveyFull,
        data
      })
      pushHistory('/complete')
    }
  }

  // Wait for /locales (so we can decide whether to show the selector) and for
  // the questionnaire fetch once it's been kicked off. With the deferral in
  // AppState, the questionnaire request is itself gated on language being
  // resolved, so `loading` is naturally false on the selector page.
  if (surveyLocalesLoading || loading) {
    return <LoadingOverlay />
  }

  // A /locales failure is not fatal: AppState falls back to a no-language
  // questionnaire fetch (server returns the primary locale), so the survey
  // still works for single-language and primary-language respondents. Only a
  // questionnaire error blocks the survey.
  if (error) {
    return <ErrorOverlay />
  }

  if (shouldProceedWithFdchat) {
    return <FdChat />
  }

  const redirectTo = getPageRedirect()

  if (redirectTo) {
    return redirectTo
  }

  return (
    <PfmContext.Provider value={{ next: handleNext }}>
      {children}
    </PfmContext.Provider>
  )
}

export default PageFlowManager
